import { createConsumer } from '@rails/actioncable'
import type { Consumer } from '@rails/actioncable'
import type { InjectionKey } from 'vue'
import { Capacitor } from '@capacitor/core'
import { SOCKET_ID } from '@/lib/socketId'

export interface OrgChannelMessage {
  // `socket_id`: the tab whose request caused the change (lib/socketId);
  // absent when a job, a webhook or the public signing page caused it.
  updates: Array<{ type: string; id: string; socket_id?: string }>
}

type MessageHandler = (msg: OrgChannelMessage) => void

export interface OnMessageOptions {
  // Receive this tab's own writes too. For a listener that is NOT a refetch
  // of something this tab already re-read — the car screen is a separate
  // surface, and the technician's own stop completion must still nudge it.
  includeOwn?: boolean
}

// ⭐ A TAB NEVER HEARS ITS OWN ECHO. Every write already refetched what it
// changed the moment it returned; the broadcast of that same write lands ~2 s
// later and would re-run the same fetches. The filter lives HERE, once, so
// every listener gets it — and a surface that learns about its own tab's write
// ONLY through the channel is now a bug: it must refresh after the write.
const withoutOwn = (msg: OrgChannelMessage): OrgChannelMessage =>
  ({ ...msg, updates: msg.updates.filter(u => u.socket_id !== SOCKET_ID) })

export function useOrganizationChannel() {
  let consumer: Consumer | null = null
  // Handlers survive across reconnects — they are app-level, not connection-level.
  const handlers = new Map<MessageHandler, OnMessageOptions>()

  function connect(token: string, orgPublicId: string) {
    // Same base resolution as api/client.ts: the native handheld serves from
    // a capacitor:// origin, where a relative /cable would target the app
    // shell, not the API. actioncable converts http(s) → ws(s) itself.
    const base = Capacitor.isNativePlatform()
      ? (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000')
      : ''
    // ⚠ auth.token is the full Authorization VALUE — « Bearer eyJ… » (the
    // server mints it prefixed so the HTTP client can pass it verbatim). The
    // cable param wants the bare JWT: unstripped, the server's JWT.decode
    // rejected EVERY connection this app ever made — silently, while
    // broadcasts fired into an empty room.
    const bareToken = token.replace(/^Bearer\s+/i, '')
    const url = `${base}/cable?token=${encodeURIComponent(bareToken)}`
    consumer = createConsumer(url)

    consumer.subscriptions.create(
      { channel: 'OrganizationChannel', org_id: orgPublicId },
      {
        received(data: OrgChannelMessage) {
          const foreign = withoutOwn(data)
          handlers.forEach((options, handler) => {
            if (options.includeOwn) handler(data)
            // A message that was nothing but our own echo is no message.
            else if (foreign.updates.length) handler(foreign)
          })
        },
      }
    )
  }

  /**
   * Disconnects the WebSocket but keeps message handlers registered.
   * Handlers survive reconnects (e.g., org switch).
   * Call teardown() on app unmount to fully clean up.
   */
  function disconnect() {
    consumer?.disconnect()
    consumer = null
  }

  /**
   * Disconnects and removes all handlers. Call on app unmount.
   */
  function teardown() {
    disconnect()
    handlers.clear()
  }

  function onMessage(handler: MessageHandler, options: OnMessageOptions = {}): () => void {
    handlers.set(handler, options)
    return () => handlers.delete(handler)
  }

  return { connect, disconnect, teardown, onMessage }
}

// ── Injection key — lets child composables get the app-level singleton ──
export type OrgChannel = ReturnType<typeof useOrganizationChannel>
export const ORG_CHANNEL_KEY: InjectionKey<OrgChannel> = Symbol('orgChannel')
